import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import type { TransactionHash } from "@/types";

/**
 * Prepares a start unwinding transaction
 */
export const prepareStartUnwindingTransaction = async (
    shares: bigint,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "startUnwinding",
            args: [shares, unwindingEpochs],
        }),
    };
};

/**
 * Estimates gas for a start unwinding transaction
 */
export const estimateStartUnwindingTransactionGas = async (
    shares: bigint,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareStartUnwindingTransaction(shares, unwindingEpochs, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};

export const gatewayStartUnwindingTransaction = async (
    shares: bigint,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareStartUnwindingTransaction(shares, unwindingEpochs, walletClient);
    return writeContractTransaction(walletClient, txRequest);
};
